import { api } from "_shared/_stores/api";
import { AxiosResponse } from "axios";
import AssistantAPICommand from "ai-assistant/_api/commands/assistant-api-command";
import AssistantResponse from "ai-assistant/assistant-response";
import { AssistantResponseCodec, AssistantResponseParams } from "ai-assistant/assistant-response-schema";
import AssistantAttachment from "ai-assistant/attachments/assistant-attachment";
import { AssistantAttachmentCodec, AssistantAttachmentParams } from "ai-assistant/attachments/assistant-attachment-schema";
import AssistantConversation from "ai-assistant/conversations/assistant-conversation";
import { AssistantConversationParams, AssistantConversationWireZ } from "ai-assistant/conversations/assistant-conversation-schema";
import AnswerFeedback from "ai-assistant/feedback/answer-feedback";
import { AnswerFeedbackCategory, AnswerFeedbackReason } from "ai-assistant/feedback/answer-feedback-reason";
import AnswerFeedbackReport, { AnswerFeedbackReportJSON } from "ai-assistant/feedback/answer-feedback-report";
import AnswerFeedbackSchema, { AnswerFeedbackJSON } from "ai-assistant/feedback/answer-feedback-schema";
import AssistantConversationSummary, { AssistantConversationSummarySchema, AssistantConversationSummaryJSON } from "ai-assistant/conversations/assistant-conversation-summary";
import { AssistantScreenContext, boundAssistantScreenContext } from "ai-assistant/screen-context/assistant-screen-context";
import { documentListTag } from "documents/_api/document-api";
import { readDownloadErrorBody } from "documents/_api/document-download-failure";
import { z } from "zod";

export interface SendAssistantMessageRequest {
  message: string;
  conversationId?: string;
  currentRoute?: string;
  screenContext?: AssistantScreenContext;
  /** Files uploaded through `uploadAssistantAttachment` for this message; at most five. */
  attachmentIds?: string[];
  /** Names the turn whose live steps the panel is listening for (#7286). */
  turnId?: string;
}

export interface UploadAssistantAttachmentRequest {
  file: File;
}

export interface AssistantAttachmentRequest {
  attachmentId: string;
}

export interface StagedActionRequest {
  stagedActionId: string;
}

export interface AssistantConversationRequest {
  conversationId: string;
}

export interface ReportAssistantAnswerRequest {
  conversationId: string;
  /** The trace id the answer's reply carried. */
  traceId: string;
  reason: AnswerFeedbackReason;
  comment?: string;
}

/** What a bulk erasure left behind, and why. */
export interface AssistantHistoryErasure {
  deleted: number;
  retainedUnderLegalHold: number;
}

export const aiAssistantApi = api.injectEndpoints({
  endpoints: (build) => ({
    sendAssistantMessage: build.mutation<AssistantResponse, SendAssistantMessageRequest>({
      query: (request) => ({
        url: "ai-assistant",
        method: "POST",
        body: toAssistantAPICommand(request),
      }),
      // Validated and decoded where it arrives, once; see `AssistantResponseCodec` (#5992).
      rawResponseSchema: AssistantResponseCodec,
      transformResponse: (response: AssistantResponseParams): AssistantResponse => new AssistantResponse(response),
      invalidatesTags: (response) => response === undefined
        ? []
        : [
          { type: "AssistantConversation", id: response.conversationId },
          { type: "AssistantConversation", id: "LIST" },
        ],
    }),
    /**
     * Performs work the assistant prepared and the user has approved.
     *
     * No body: the arguments come from the staged action the server holds. Sending them
     * would turn this into a general-purpose write route that bypasses the real command
     * surface and its validation.
     */
    confirmStagedAction: build.mutation<AssistantResponse, StagedActionRequest>({
      query: ({ stagedActionId }) => ({
        url: `ai-assistant/staged-actions/${stagedActionId}/confirm`,
        method: "POST",
      }),
      // Validated and decoded where it arrives, once; see `AssistantResponseCodec` (#5992).
      rawResponseSchema: AssistantResponseCodec,
      transformResponse: (response: AssistantResponseParams): AssistantResponse => new AssistantResponse(response),
      // Coarse on purpose while there are few capabilities: a team template, or an attachment
      // filed with a work's documents (#6504), which the work's Documents panel lists. A
      // capability-to-tags map is the shape this wants when there are more.
      invalidatesTags: [{ type: "TeamTemplate", id: "LIST" }, documentListTag],
    }),
    /**
     * The caller's own conversations, most recently used first.
     *
     * No user or legal entity in the request. Both come from the token on the server, so there
     * is no parameter here that could widen what it returns.
     */
    listAssistantConversations: build.query<AssistantConversationSummary[], void>({
      query: () => ({ url: "ai-assistant/conversations", method: "GET" }),
      rawResponseSchema: z.array(AssistantConversationSummarySchema.AssistantConversationSummaryJSONZ),
      transformResponse: (response: AssistantConversationSummaryJSON[]): AssistantConversationSummary[] =>
        response.map((summary) => AssistantConversationSummary.fromJSON(summary)),
      providesTags: [{ type: "AssistantConversation", id: "LIST" }],
    }),
    /**
     * One conversation, with every citation re-checked against what the caller may read now.
     *
     * Not cached across a delete or a new turn - both invalidate it - because the point of the
     * read-time check is that its answer can change between one read and the next.
     */
    readAssistantConversation: build.query<AssistantConversation, AssistantConversationRequest>({
      query: ({ conversationId }) => ({
        url: `ai-assistant/conversations/${conversationId}`,
        method: "GET",
      }),
      // Validated and decoded where it arrives, once; see `AssistantConversationWireZ` (#5992).
      rawResponseSchema: AssistantConversationWireZ,
      transformResponse: (response: AssistantConversationParams): AssistantConversation =>
        new AssistantConversation(response),
      providesTags: (_result, _error, { conversationId }) => [
        { type: "AssistantConversation", id: conversationId },
      ],
    }),
    /**
     * Erases one conversation, its turns, its citation excerpts and anything staged in it.
     *
     * Invalidates the list and not the conversation itself. The panel holds a subscription to the
     * conversation it last opened, and invalidating a deleted one makes RTK Query read it again -
     * which can only answer 404, and did, in the browser (#5071).
     */
    deleteAssistantConversation: build.mutation<void, AssistantConversationRequest>({
      query: ({ conversationId }) => ({
        url: `ai-assistant/conversations/${conversationId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AssistantConversation", id: "LIST" }],
    }),
    /**
     * Erases every conversation the caller has here.
     *
     * Answers with what it left: conversations under legal hold survive an erasure request, and
     * telling the user their history is gone when some of it is not would be the wrong answer.
     */
    deleteAllAssistantConversations: build.mutation<AssistantHistoryErasure, void>({
      query: () => ({ url: "ai-assistant/conversations", method: "DELETE" }),
      invalidatesTags: [{ type: "AssistantConversation", id: "LIST" }],
    }),
    /**
     * Reports that one answer was wrong, and why (#5204). Reporting it again replaces the reason.
     *
     * The answer is named by the trace id its reply carried. Invalidates the conversation so a
     * later read of it replays the report, and the queue a super-user may have open.
     */
    reportAssistantAnswer: build.mutation<AnswerFeedback, ReportAssistantAnswerRequest>({
      query: ({ conversationId, traceId, reason, comment }) => ({
        url: `ai-assistant/conversations/${conversationId}/feedback`,
        method: "POST",
        body: { traceId, reason, comment },
      }),
      rawResponseSchema: AnswerFeedbackSchema.AnswerFeedbackJSONZ,
      transformResponse: (response: AnswerFeedbackJSON): AnswerFeedback =>
        new AnswerFeedback(AnswerFeedbackSchema.decodeParsed(response)),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "AssistantConversation", id: conversationId },
        { type: "AssistantFeedback", id: "LIST" },
      ],
    }),
    /**
     * The most recent reports in one queue. Super-users only; anyone else is refused with 403.
     *
     * One queue per read, so the access queue is never a filter left off the quality one.
     */
    listAssistantFeedback: build.query<AnswerFeedbackReport[], AnswerFeedbackCategory>({
      query: (category) => ({ url: "ai-assistant/feedback", method: "GET", params: { category } }),
      rawResponseSchema: z.array(AnswerFeedbackReport.AnswerFeedbackReportJSONZ),
      transformResponse: (response: AnswerFeedbackReportJSON[]): AnswerFeedbackReport[] =>
        response.map((report) => AnswerFeedbackReport.fromJSON(report)),
      providesTags: [{ type: "AssistantFeedback", id: "LIST" }],
    }),
    /**
     * Uploads one file to attach to the next message (#6504). The message names it by the id this
     * answers with; the turn itself stays JSON.
     *
     * Refusals are problem details: 400 `file_too_small`, 413 `file_too_large`, 415
     * `unsupported_file_type` or `file_type_mismatch`.
     */
    uploadAssistantAttachment: build.mutation<AssistantAttachment, UploadAssistantAttachmentRequest>({
      query: ({ file }) => ({
        url: "ai-assistant/attachments",
        method: "POST",
        body: attachmentFormData(file),
      }),
      rawResponseSchema: AssistantAttachmentCodec,
      transformResponse: (response: AssistantAttachmentParams): AssistantAttachment => new AssistantAttachment(response),
    }),
    /**
     * Takes back a file before the message that would carry it is sent. One a message already
     * sent is part of that conversation, and is refused with 409 `attachment_already_sent`.
     */
    removeAssistantAttachment: build.mutation<void, AssistantAttachmentRequest>({
      query: ({ attachmentId }) => ({
        url: `ai-assistant/attachments/${attachmentId}`,
        method: "DELETE",
      }),
    }),
    /**
     * An attached file's bytes, for its owner alone.
     *
     * No tags and dropped as soon as nothing subscribes, as a document download is: a download is
     * an act, and the entry holds the file's bytes (#5992).
     */
    downloadAssistantAttachment: build.query<AxiosResponse<Blob>, AssistantAttachmentRequest>({
      query: ({ attachmentId }) => ({
        url: `ai-assistant/attachments/${attachmentId}/content`,
        responseHandler: async (response: Response) => {
          // A refusal carries its reason; read as a blob, it would never reach the error reader.
          if (!response.ok) return readDownloadErrorBody(response);

          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });

          return {
            data: await response.blob(),
            headers,
            request: {
              getResponseHeader: (name: string) => response.headers.get(name),
            },
          } as AxiosResponse<Blob>;
        },
      }),
      keepUnusedDataFor: 0,
    }),
    /** Drops a prepared action. Nothing was written, so nothing needs invalidating. */
    cancelStagedAction: build.mutation<AssistantResponse, StagedActionRequest>({
      query: ({ stagedActionId }) => ({
        url: `ai-assistant/staged-actions/${stagedActionId}/cancel`,
        method: "POST",
      }),
      // Validated and decoded where it arrives, once; see `AssistantResponseCodec` (#5992).
      rawResponseSchema: AssistantResponseCodec,
      transformResponse: (response: AssistantResponseParams): AssistantResponse => new AssistantResponse(response),
    }),
  }),
});

export const {
  useSendAssistantMessageMutation,
  useConfirmStagedActionMutation,
  useCancelStagedActionMutation,
  useListAssistantConversationsQuery,
  useLazyReadAssistantConversationQuery,
  useDeleteAssistantConversationMutation,
  useDeleteAllAssistantConversationsMutation,
  useReportAssistantAnswerMutation,
  useListAssistantFeedbackQuery,
  useUploadAssistantAttachmentMutation,
  useRemoveAssistantAttachmentMutation,
} = aiAssistantApi;

function toAssistantAPICommand(request: SendAssistantMessageRequest): AssistantAPICommand {
  return {
    message: request.message,
    conversationId: request.conversationId,
    currentRoute: request.currentRoute,
    screenContext: request.screenContext && boundAssistantScreenContext(request.screenContext),
    timeZone: browserTimeZone(),
    attachmentIds: request.attachmentIds?.length ? request.attachmentIds : undefined,
    turnId: request.turnId,
  };
}

/** One file in the `file` field, which is the field the attachments endpoint binds. */
function attachmentFormData(file: File): FormData {
  const formData = new FormData();
  formData.append("file", file, file.name);
  return formData;
}

/**
 * The browser's IANA time zone, or nothing when it reports none.
 *
 * Sent with every turn so a summary grouped by month or limited to "this year" uses the user's
 * calendar rather than UTC's. The server treats it as a hint: an id it does not recognise is UTC.
 */
function browserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}
